import { TextField } from '@mui/material'
import { IdentityEvents } from '@serverlessinc/core-events'
import { useAuthUrlQuery } from 'auth/hook/useAuthUrlQuery'
import { session } from 'auth/session'
import { createUserAndOrg, nonBrowserCallback } from 'auth/utils'
import { FormWrapper } from 'auth/utils/FormWrapper'
import { apiClient } from 'common/api'
import Button from 'common/components/Button'
import { Card } from 'common/components/Card'
import { ErrorMessage } from 'common/components/ErrorMessage'
import { Link } from 'common/components/Link'
import { serverless } from 'common/sdk'
import { publishEvent } from 'common/utils/events'
import { getErrorMessage } from 'common/utils/getErrorMessage'
import queryString from 'query-string'
import { useState } from 'react'

export const UserNameView = () => {
  const [orgName, setOrgName] = useState('')
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(false)
  const { setQuery } = useAuthUrlQuery()

  const handleLogout = () => {
    session.destroy()
  }
  /**
   * After registration is complete, wrap things up
   */
  const finishRegistration = async () => {
    const sessionData = session.getAll()

    const me = await apiClient({
      url: 'core/me',
      headers: {
        Authorization: `Bearer ${sessionData.authIdToken}`,
      },
    })
    session.set('user', me)

    /**
     * If a non-browser-based client is trying to log in, perform callback via websockets.
     */
    if (sessionData?.authClient === 'cli' && sessionData.authClientTransactionId) {
      await nonBrowserCallback(sessionData.authClientTransactionId, {
        access_token: sessionData.authAccessToken,
        id_token: sessionData.authIdToken,
        refresh_token: sessionData.authRefreshToken,
        expires_at: sessionData.authExpires,
        username: me.userName,
        user_uid: me.userUid,
      })
    }

    /**
     * Track Registration Details
     */

    // Save referrer info that caused the user to sign up in User Meta
    await serverless.saveUserMeta({
      dashboard: 'v2',
      referrer: {
        client: sessionData.authClient || null,
        packageName: sessionData.referrerPackage || null,
        source: sessionData.referrerSource || null,
        medium: sessionData.referrerMedium || null,
        campaign: sessionData.referrerCampaign || null,
        content: sessionData.referrerContent || null,
        term: sessionData.referrerTerm || null,
      },
    })

    // If CLI login, let the user know by adding the ?client=cli param back for the next view
    if (session.get('authClient')) {
      setQuery({
        client: session.get('authClient'),
      })
    }

    /**
     * If that works, then get the orgs for the user
     */
    const orgs = await apiClient({
      url: 'core/tenants',
      params: {
        userName: me.userName,
      },
      headers: {
        Authorization: `Bearer ${sessionData.authIdToken}`,
      },
    })

    /**
     * Hopefully there should be a case where you're an owner of some org
     */
    let currentOrg = orgs.find((org) => org.role === 'owner')

    /**
     * If there's some weird case where you're not an owner, just pick the first org
     */
    if (!currentOrg) {
      currentOrg = orgs[0]
    }

    try {
      /**
       * Tracking (Google Analytics)
       */
      let event = new Event({
        name: 'auth_registration',
        orgName: currentOrg.orgName,
      })
      event.publish()
    } catch (err) {
      console.error('Failed to publish auth_registration event', err)
    }
    let q = queryString.parse(sessionData.authRedirectQuery)

    delete q.code
    delete q.state
    delete q.utm_source
    delete q.utm_medium
    delete q.utm_campaign
    delete q.utm_content
    delete q.utm_term
    delete q.view
    delete q.socialAuthType

    /**
     * Set the invitation code for /join
     */
    if (sessionData.authJoinOrganizationInvitationToken) {
      q.code = sessionData.authJoinOrganizationInvitationToken
    }

    /**
     * Tracking (Event bus)
     */
    publishEvent(
      new IdentityEvents.UserAuthenticatedV1Event({
        userId: session.get('user').userUid,
        queryParameters: q,
      })
    )

    const stringQuery = queryString.stringify(q)
    q = stringQuery ? `?${stringQuery}` : ''

    /**
     * Hard refresh to show the app
     */
    const path = sessionData.authRedirectPath || `/${currentOrg.orgName || ''}`

    window.location.href = `${path}${q}`
  }

  /**
   * Handles when a user submits a custom User/Org name
   */
  const handleUsernameFormSubmit = async (event) => {
    event.preventDefault()

    setLoading(true)

    try {
      await createUserAndOrg(orgName)
      await finishRegistration()
    } catch (err) {
      setError(getErrorMessage(err))
    }
    setLoading(false)
  }

  return (
    <FormWrapper onSubmit={handleUsernameFormSubmit}>
      <Card>
        <TextField
          label="Organization Name"
          name="orgName"
          autoFocus
          variant="outlined"
          fullWidth
          value={orgName}
          color="primary"
          onChange={(e) => {
            setError('')
            setOrgName(e.target.value)
          }}
          helperText="This can be changed later from your settings"
        />

        <Button
          loading={loading}
          color="primary"
          variant="contained"
          type="submit"
          sx={{ width: '100%', my: 2 }}
        >
          Submit
        </Button>
        <ErrorMessage message={error} />
      </Card>
      <Link
        onClick={handleLogout}
        disabled={loading}
        sx={{
          margin: '0 auto',
        }}
      >
        Logout
      </Link>
    </FormWrapper>
  )
}
